import type { ConversationHandoffRequestDto, UpdateConversationContextRequestDto } from '@/types/api-generated'
import { apiClient } from './client'
import { reportChatFailure } from '@/monitoring/chatDiagnostics'
import type {
  ConversationStartRequest,
  ConversationMessageRequest,
  ConversationApplyRequest,
  ConversationCompleteRequest,
  UpdateFeedbackRequest,
  UpdateMessageFeedbackRequest,
  AIConversationContext,
  AIConversationSessionDto,
  ConversationAttachmentDto,
} from '@/types/aiChat'

/**
 * Thrown by {@link sendMessage} when the server returns 409 because another
 * user is mid-turn on a shared asset-chat session. Carries the holder identity
 * so the caller can render "Joel is editing…" without an extra lookup.
 */
export class TurnAlreadyActiveError extends Error {
  readonly sessionId: string | null
  readonly turnUserId: string | null
  readonly turnUserName: string | null
  readonly turnStartedAt: string | null
  constructor(body: { sessionId?: string | null; turnUserId?: string | null; turnUserName?: string | null; turnStartedAt?: string | null; message?: string }) {
    super(body.message || 'Another user is asking Webster.')
    this.name = 'TurnAlreadyActiveError'
    this.sessionId = body.sessionId ?? null
    this.turnUserId = body.turnUserId ?? null
    this.turnUserName = body.turnUserName ?? null
    this.turnStartedAt = body.turnStartedAt ?? null
  }
}

/**
 * Thrown by {@link sendMessage} when the server returns 410 because the
 * conversation session has expired (ExpiresAtUtc passed). The caller should
 * start a fresh session rather than retry.
 */
export class ConversationExpiredError extends Error {
  readonly sessionId: string | null
  readonly expiredAtUtc: string | null
  constructor(body: { sessionId?: string | null; expiredAtUtc?: string | null; message?: string }) {
    super(body.message || 'This conversation has expired. Start a new one.')
    this.name = 'ConversationExpiredError'
    this.sessionId = body.sessionId ?? null
    this.expiredAtUtc = body.expiredAtUtc ?? null
  }
}

/**
 * The middleware wraps error bodies as `{ error: "<json string>", stackTrace: "" }`,
 * so the structured fields land on `result.error` as a JSON string. Try to parse
 * it and return the inner object; fall through to a plain shape if parsing fails.
 */
function parseStructuredError(raw: unknown): Record<string, unknown> {
  if (typeof raw !== 'string') return {}
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed as Record<string, unknown> : {}
  } catch {
    return {}
  }
}

/**
 * Start a new AI conversation
 */
export async function startConversation(
  clientCompanyId: string,
  workspaceId: string,
  context: AIConversationContext,
  options: {
    newThread?: boolean
    parentSessionId?: string | null
    /** The level the knowledge base agent works at in this thread, which a start alone can set (ORB-1027). */
    knowledgeBaseLevel?: ConversationStartRequest['KnowledgeBaseLevel']
  } = {}
): Promise<AIConversationSessionDto> {
  // The API opens a new session on every call and resumes a thread only by id, so NewThread changes
  // nothing today. It is a non-nullable bool on the C# side, so it is always sent, as the contract has it.
  const request: ConversationStartRequest = {
    Context: context,
    NewThread: options.newThread ?? false,
    ParentSessionId: options.parentSessionId || undefined,
    KnowledgeBaseLevel: options.knowledgeBaseLevel || undefined,
  }

  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations`,
    {
      method: 'POST',
      body: request,
      requiresAuth: true,
    }
  )

  if (response.error || !response.data) {
    throw new Error(response.error || 'Failed to start conversation')
  }

  return response.data
}

/**
 * Fetch an existing AI conversation session (and its messages) by id.
 * Used to restore Webster chat history on page reload.
 */
export async function getConversation(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string
): Promise<AIConversationSessionDto | null> {
  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}`,
    {
      method: 'GET',
      requiresAuth: true,
    }
  )

  if (response.error === 'Resource not found') return null
  if (response.error || !response.data) {
    throw new Error(response.error || 'Failed to load conversation')
  }

  return response.data
}

/** Persist an accepted list on the same Webster thread, without generating a message. */
export async function updateConversationContext(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  request: UpdateConversationContextRequestDto,
): Promise<AIConversationSessionDto> {
  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Context`,
    { method: 'PUT', body: request, requiresAuth: true },
  )
  if (response.status === 412) throw new Error('This conversation changed. Try opening the list again.')
  if (response.error || !response.data) throw new Error(response.error || 'The list could not be saved. Try again.')
  return response.data
}

/**
 * Leaves a line on a conversation whose task moved to another surface (ORB-819).
 */
export async function recordConversationHandoff(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  request: ConversationHandoffRequestDto,
): Promise<AIConversationSessionDto> {
  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Handoffs`,
    { method: 'POST', body: request, requiresAuth: true },
  )
  if (response.error || !response.data) throw new Error(response.error || 'This could not be moved. Try again.')
  return response.data
}

/**
 * Send a message in an existing conversation
 */
export async function sendMessage(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  message: string,
  updatedContext?: AIConversationContext,
  attachments?: ConversationAttachmentDto[]
): Promise<AIConversationSessionDto> {
  const request: ConversationMessageRequest = {
    Message: message,
    UpdatedContext: updatedContext,
    Attachments: attachments && attachments.length > 0 ? attachments : undefined,
  }

  const startedAt = performance.now()
  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Messages`,
    {
      method: 'POST',
      body: request,
      requiresAuth: true,
    }
  )

  // Surface multi-user turn-lock + session-expiration as typed errors so the
  // caller can render holder-aware messages ("Joel is editing…") instead of a
  // generic toast. Race window is small in practice but the typed errors keep
  // the failure path coherent when it does fire.
  if (response.status === 409) {
    const body = parseStructuredError(response.error)
    if (body.error === 'TurnAlreadyActive') {
      throw new TurnAlreadyActiveError({
        sessionId: body.sessionId as string | undefined,
        turnUserId: body.turnUserId as string | undefined,
        turnUserName: body.turnUserName as string | undefined,
        turnStartedAt: body.turnStartedAt as string | undefined,
        message: body.message as string | undefined,
      })
    }
  }
  if (response.status === 410) {
    const body = parseStructuredError(response.error)
    if (body.error === 'ConversationExpired') {
      throw new ConversationExpiredError({
        sessionId: body.sessionId as string | undefined,
        expiredAtUtc: body.expiredAtUtc as string | undefined,
        message: body.message as string | undefined,
      })
    }
  }

  if (response.error || !response.data) {
    reportChatFailure('send-message', {
      sessionId, status: response.status, elapsedMs: Math.round(performance.now() - startedAt),
    })
    throw new Error(response.error || 'Failed to send message')
  }

  return response.data
}

/**
 * Apply AI-generated content to a field
 */
export async function applyToField(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  fieldName: string,
  value: string,
  variationIndex?: number,
  messageIndex?: number
): Promise<AIConversationSessionDto> {
  const request: ConversationApplyRequest = {
    MessageIndex: messageIndex ?? 0,
    FieldName: fieldName,
    Value: value,
    VariationIndex: variationIndex,
  }

  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Apply`,
    {
      method: 'POST',
      body: request,
      requiresAuth: true,
    }
  )

  if (response.error || !response.data) {
    throw new Error(response.error || 'Failed to apply value')
  }

  return response.data
}

/**
 * Complete/end a conversation
 */
export async function completeConversation(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  action: 'Applied' | 'Dismissed' | 'Abandoned',
  selectedContent?: string,
  selectedVariationIndex?: number,
  rating?: boolean | null
): Promise<AIConversationSessionDto> {
  const request: ConversationCompleteRequest = {
    Action: action,
    SelectedContent: selectedContent,
    SelectedVariationIndex: selectedVariationIndex,
    Rating: rating,
  }

  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Complete`,
    {
      method: 'POST',
      body: request,
      requiresAuth: true,
    }
  )

  if (response.error || !response.data) {
    throw new Error(response.error || 'Failed to complete conversation')
  }

  return response.data
}

/**
 * Update context in an ongoing conversation
 * (sends a message with updated context)
 */
export async function updateContext(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  context: AIConversationContext
): Promise<void> {
  await sendMessage(clientCompanyId, workspaceId, sessionId, '[Context Updated]', context)
}

/**
 * Update feedback for a completed conversation
 */
export async function updateFeedback(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  rating?: boolean | null,
  feedbackComment?: string | null
): Promise<AIConversationSessionDto> {
  const request: UpdateFeedbackRequest = {
    Rating: rating,
    FeedbackComment: feedbackComment,
  }

  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Feedback`,
    {
      method: 'POST',
      body: request,
      requiresAuth: true,
    }
  )

  if (response.error || !response.data) {
    throw new Error(response.error || 'Failed to update feedback')
  }

  return response.data
}

/**
 * Update feedback for a specific message in a conversation
 */
export async function updateMessageFeedback(
  clientCompanyId: string,
  workspaceId: string,
  sessionId: string,
  messageIndex: number,
  rating?: boolean | null,
  feedbackComment?: string | null
): Promise<AIConversationSessionDto> {
  const request: UpdateMessageFeedbackRequest = {
    Rating: rating,
    FeedbackComment: feedbackComment,
  }

  const response = await apiClient.request<AIConversationSessionDto>(
    `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Messages/${messageIndex}/Feedback`,
    {
      method: 'PUT',
      body: request,
      requiresAuth: true,
    }
  )

  if (response.error || !response.data) {
    throw new Error(response.error || 'Failed to update message feedback')
  }

  return response.data
}
